import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowPathIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Alert, Badge, Button, Card } from '../components/common/UIComponents';
import { PageEmptyState, PageLoadingState } from '../components/common/ViewStates';
import { ServiceWorkspaceShell } from '../components/Service/ServiceWorkspaceShell';
import { UserRole, EquipmentTypeSuggestion } from '../types/firestore';
import { getActiveEquipmentTypeCatalog } from '../services/equipmentTypeCatalogService';
import {
  getEquipmentTypeSuggestionsForCurrentUser,
  submitEquipmentTypeSuggestion,
} from '../services/equipmentTypeSuggestionService';
import { notificationService } from '../services/notificationService';
import { type EquipmentTypeCatalogEntry } from '../utils/equipmentTypeCatalog';

type SuggestionDraft = {
  suggestedName: string;
  notes: string;
};

const normalizeInput = (value: string): string => value.trim().replace(/\s+/g, ' ');

const formatTimestamp = (value?: EquipmentTypeSuggestion['createdAt']) => {
  if (!value || typeof (value as { toDate?: () => Date }).toDate !== 'function') {
    return 'Brak daty';
  }
  return (value as { toDate: () => Date }).toDate().toLocaleString('pl-PL');
};

const getFriendlyError = (fallback: string, err: unknown): string => {
  const code = String((err as { code?: string })?.code || '').toLowerCase();
  if (code.includes('permission-denied')) {
    return 'Brak uprawnień do tej operacji.';
  }
  if (code.includes('already-exists')) {
    return 'Podobny typ sprzętu już istnieje albo oczekuje na moderację.';
  }
  if (code.includes('failed-precondition')) {
    return 'Nie można wykonać tej operacji w obecnym stanie.';
  }
  return fallback;
};

const getEquipmentTypeSourceBadge = (source: EquipmentTypeCatalogEntry['source']) =>
  source === 'suggestion' ? (
    <Badge variant="info" size="sm">
      Z sugestii
    </Badge>
  ) : (
    <Badge variant="neutral" size="sm">
      Admin
    </Badge>
  );

const getSuggestionStatusBadge = (status: EquipmentTypeSuggestion['status']) => {
  if (status === 'accepted') {
    return (
      <Badge variant="success" size="sm">
        Akceptowana
      </Badge>
    );
  }
  if (status === 'rejected') {
    return (
      <Badge variant="error" size="sm">
        Odrzucona
      </Badge>
    );
  }
  return (
    <Badge variant="warning" size="sm">
      Pending
    </Badge>
  );
};

const ServiceEquipmentTypesPage: React.FC = () => {
  const { currentUser, userProfile } = useAuth();
  const [searchParams] = useSearchParams();
  const requestedSuggestionId = String(searchParams.get('equipmentTypeSuggestionId') || '').trim();
  const requestedNotificationId = String(searchParams.get('notificationId') || '').trim();
  const [catalog, setCatalog] = useState<EquipmentTypeCatalogEntry[]>([]);
  const [mySuggestions, setMySuggestions] = useState<EquipmentTypeSuggestion[]>([]);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [loadingSuggestions, setLoadingSuggestions] = useState(true);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [suggestionsError, setSuggestionsError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [draft, setDraft] = useState<SuggestionDraft>({ suggestedName: '', notes: '' });
  const [error, setError] = useState<string | null>(null);

  const canSuggestEquipmentTypes = userProfile?.role === UserRole.SERVICE_ADMIN;

  const loadCatalog = useCallback(async () => {
    setLoadingCatalog(true);
    setCatalogError(null);
    try {
      const nextCatalog = await getActiveEquipmentTypeCatalog({ forceRefresh: true });
      setCatalog(nextCatalog.entries);
    } catch (err) {
      console.error('Failed to load active equipment type catalog:', err);
      setCatalogError('Nie udało się załadować katalogu typów sprzętu.');
    } finally {
      setLoadingCatalog(false);
    }
  }, []);

  const loadSuggestions = useCallback(async () => {
    setLoadingSuggestions(true);
    setSuggestionsError(null);
    try {
      if (!currentUser?.uid) {
        setMySuggestions([]);
        return;
      }
      const nextSuggestions = await getEquipmentTypeSuggestionsForCurrentUser(currentUser.uid);
      setMySuggestions(nextSuggestions);
    } catch (err) {
      console.error('Failed to load equipment type suggestions:', err);
      setSuggestionsError('Nie udało się załadować Twoich sugestii typów sprzętu.');
    } finally {
      setLoadingSuggestions(false);
    }
  }, [currentUser?.uid]);

  const refreshData = useCallback(async () => {
    await Promise.all([loadCatalog(), loadSuggestions()]);
  }, [loadCatalog, loadSuggestions]);

  useEffect(() => {
    void refreshData();
  }, [refreshData]);

  useEffect(() => {
    if (
      !requestedSuggestionId ||
      !requestedNotificationId ||
      loadingSuggestions ||
      Boolean(suggestionsError)
    ) {
      return;
    }

    const exactSuggestion = mySuggestions.find(
      (suggestion) => suggestion.equipmentTypeSuggestionId === requestedSuggestionId
    );
    if (
      !exactSuggestion ||
      (exactSuggestion.status !== 'accepted' && exactSuggestion.status !== 'rejected')
    ) {
      return;
    }

    const hasResolvedAt =
      typeof (exactSuggestion.resolvedAt as { toDate?: unknown } | null)?.toDate === 'function';
    const hasAcceptedSnapshot =
      exactSuggestion.status !== 'accepted' ||
      Boolean(
        exactSuggestion.canonicalEquipmentTypeId &&
          exactSuggestion.canonicalName &&
          exactSuggestion.canonicalShortCode
      );
    if (!hasResolvedAt || !hasAcceptedSnapshot) {
      return;
    }

    void notificationService
      .markEquipmentTypeSuggestionOutcomeNotificationAsRead({
        notificationId: requestedNotificationId,
        suggestionId: requestedSuggestionId,
        outcome: exactSuggestion.status,
      })
      .catch((error) => {
        console.error('Failed to mark equipment type suggestion outcome notification:', error);
      });
  }, [
    loadingSuggestions,
    mySuggestions,
    requestedNotificationId,
    requestedSuggestionId,
    suggestionsError,
  ]);

  const filteredCatalog = useMemo(() => {
    const query = catalogSearch.trim().toLowerCase();
    if (!query) {
      return catalog;
    }
    return catalog.filter((item) =>
      [item.name, item.shortCode, item.status, item.source].join(' ').toLowerCase().includes(query)
    );
  }, [catalog, catalogSearch]);

  const catalogStats = useMemo(
    () => ({
      total: catalog.length,
      active: catalog.filter((item) => item.status === 'active').length,
    }),
    [catalog]
  );

  const suggestionStats = useMemo(
    () => ({
      total: mySuggestions.length,
      pending: mySuggestions.filter((item) => item.status === 'pending').length,
      accepted: mySuggestions.filter((item) => item.status === 'accepted').length,
      rejected: mySuggestions.filter((item) => item.status === 'rejected').length,
    }),
    [mySuggestions]
  );

  const handleSubmitSuggestion = async (event: React.FormEvent) => {
    event.preventDefault();
    const suggestedName = normalizeInput(draft.suggestedName);
    const notes = normalizeInput(draft.notes);
    if (!suggestedName) {
      setError('Podaj nazwę nowego typu sprzętu.');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await submitEquipmentTypeSuggestion({
        suggestedName,
        notes,
      });
      setDraft({ suggestedName: '', notes: '' });
      await loadSuggestions();
    } catch (err) {
      console.error('Failed to submit equipment type suggestion:', err);
      setError(getFriendlyError('Nie udało się wysłać sugestii typu sprzętu.', err));
    } finally {
      setBusy(false);
    }
  };

  if (loadingCatalog && loadingSuggestions && catalog.length === 0 && mySuggestions.length === 0) {
    return (
      <div className="space-y-4">
        <PageLoadingState
          title="Ładowanie typów sprzętu"
          message="Pobieramy canonical katalog i Twoje sugestie."
        />
      </div>
    );
  }

  return (
    <ServiceWorkspaceShell contentClassName="space-y-0 p-0 sm:space-y-0 sm:p-0">
      <div className="space-y-4 px-1 pb-1 pt-1 sm:px-1.5 sm:pb-1.5 sm:pt-1.5">
        <Card className="p-4 sm:p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-semibold text-primary-light-100">Typy sprzętu</h1>
                <Badge variant="info" size="sm">
                  Canonical read model
                </Badge>
              </div>
              <p className="max-w-3xl text-sm text-primary-light-300">
                Ten ekran pokazuje aktywny katalog typów sprzętu i pozwala serviceAdmin zgłaszać
                nowe propozycje. shortCode jest stabilny i nie jest numerem zlecenia.
              </p>
            </div>

            <Button
              type="button"
              variant="neutral"
              size="sm"
              onClick={() => {
                void refreshData();
              }}
              icon={<ArrowPathIcon className="h-4 w-4" />}
            >
              Odśwież
            </Button>
          </div>
        </Card>

        {(error || catalogError || suggestionsError) && (
          <div className="space-y-2">
            {error && <Alert variant="error">{error}</Alert>}
            {catalogError && <Alert variant="error">{catalogError}</Alert>}
            {suggestionsError && <Alert variant="error">{suggestionsError}</Alert>}
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Card className="p-4">
            <p className="text-xs uppercase tracking-[0.08em] text-primary-light-400">Aktywne</p>
            <p className="mt-2 text-2xl font-semibold text-primary-light-100">
              {catalogStats.active}
            </p>
            <p className="mt-1 text-sm text-primary-light-300">pozycje w katalogu canonical</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs uppercase tracking-[0.08em] text-primary-light-400">
              Moje sugestie
            </p>
            <p className="mt-2 text-2xl font-semibold text-primary-light-100">
              {suggestionStats.total}
            </p>
            <p className="mt-1 text-sm text-primary-light-300">
              pending: {suggestionStats.pending}, accepted: {suggestionStats.accepted}
            </p>
          </Card>
          <Card className="p-4">
            <p className="text-xs uppercase tracking-[0.08em] text-primary-light-400">
              Uprawnienie
            </p>
            <p className="mt-2 text-lg font-semibold text-primary-light-100">
              {canSuggestEquipmentTypes ? 'serviceAdmin' : 'read-only'}
            </p>
            <p className="mt-1 text-sm text-primary-light-300">
              Zgłoszenia nowych typów są dostępne tylko dla serviceAdmin.
            </p>
          </Card>
        </div>

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1.2fr_0.8fr]">
          <Card className="p-4 sm:p-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-primary-light-100">Aktywny katalog</h2>
                <p className="text-sm text-primary-light-300">
                  Read path dla przyszłych pickerów i wyszukiwania.
                </p>
              </div>
              <label className="w-full max-w-sm space-y-1">
                <span className="text-sm font-medium text-primary-light-300">Szukaj</span>
                <input
                  value={catalogSearch}
                  onChange={(event) => setCatalogSearch(event.target.value)}
                  placeholder="Nazwa, shortCode, źródło..."
                  className="w-full rounded-lg border border-primary-dark-300 bg-primary-dark-100 px-3 py-2 text-sm text-primary-light-100 placeholder-primary-light-400 focus:border-ratuj-300 focus:outline-none"
                />
              </label>
            </div>

            {loadingCatalog && catalog.length === 0 ? (
              <div className="py-8">
                <PageLoadingState
                  title="Ładowanie katalogu"
                  message="Pobieramy aktywne typy sprzętu."
                />
              </div>
            ) : filteredCatalog.length === 0 ? (
              <div className="py-4">
                <PageEmptyState
                  title="Brak aktywnych typów"
                  message="Katalog jest pusty lub filtr nie zwrócił wyników."
                />
              </div>
            ) : (
              <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-2">
                {filteredCatalog.map((item) => (
                  <article
                    key={item.equipmentTypeId || item.id}
                    className="rounded-2xl border border-primary-dark-300/70 bg-primary-dark-100/55 p-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="text-base font-semibold text-primary-light-100">
                          {item.name}
                        </h3>
                        <p className="mt-1 text-sm text-primary-light-300">{item.shortCode}</p>
                      </div>
                      <Badge variant="neutral" size="sm">
                        {item.shortCode}
                      </Badge>
                    </div>

                    <div className="mt-3 flex flex-wrap gap-2">
                      {getEquipmentTypeSourceBadge(item.source || 'admin')}
                      <Badge variant="success" size="sm">
                        Aktywny
                      </Badge>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </Card>

          <div className="space-y-4">
            <Card className="p-4 sm:p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-primary-light-100">Zgłoś nowy typ</h2>
                  <p className="text-sm text-primary-light-300">
                    Propozycja trafia do kolejki moderacji admina.
                  </p>
                </div>
                <Badge variant={canSuggestEquipmentTypes ? 'success' : 'warning'} size="sm">
                  {canSuggestEquipmentTypes ? 'Dostępne' : 'Brak dostępu'}
                </Badge>
              </div>

              {canSuggestEquipmentTypes ? (
                <form className="mt-4 space-y-3" onSubmit={handleSubmitSuggestion}>
                  <label className="space-y-1">
                    <span className="text-sm font-medium text-primary-light-300">Nazwa</span>
                    <input
                      value={draft.suggestedName}
                      onChange={(event) =>
                        setDraft((current) => ({ ...current, suggestedName: event.target.value }))
                      }
                      placeholder="np. Roboty kuchenne"
                      className="w-full rounded-lg border border-primary-dark-300 bg-primary-dark-100 px-3 py-2 text-sm text-primary-light-100 placeholder-primary-light-400 focus:border-ratuj-300 focus:outline-none"
                      required
                    />
                  </label>

                  <label className="space-y-1">
                    <span className="text-sm font-medium text-primary-light-300">
                      Notatka dla admina
                    </span>
                    <textarea
                      value={draft.notes}
                      onChange={(event) =>
                        setDraft((current) => ({ ...current, notes: event.target.value }))
                      }
                      rows={4}
                      placeholder="Dlaczego ten typ powinien trafić do katalogu?"
                      className="w-full rounded-lg border border-primary-dark-300 bg-primary-dark-100 px-3 py-2 text-sm text-primary-light-100 placeholder-primary-light-400 focus:border-ratuj-300 focus:outline-none"
                    />
                  </label>

                  <div className="flex justify-end">
                    <Button
                      type="submit"
                      variant="primary"
                      size="sm"
                      disabled={busy}
                      icon={<PaperAirplaneIcon className="h-4 w-4" />}
                    >
                      Wyślij sugestię
                    </Button>
                  </div>
                </form>
              ) : (
                <Alert variant="warning" className="mt-4">
                  Tylko serviceAdmin może zgłaszać nowe typy sprzętu. Ten ekran pozostaje read-only.
                </Alert>
              )}
            </Card>

            <Card className="p-4 sm:p-5">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-primary-light-100">Moje sugestie</h2>
                  <p className="text-sm text-primary-light-300">
                    Historia decyzji admina dla zgłoszonych typów sprzętu.
                  </p>
                </div>
                <Badge variant="info" size="sm">
                  {suggestionStats.total}
                </Badge>
              </div>

              {loadingSuggestions && mySuggestions.length === 0 ? (
                <div className="py-8">
                  <PageLoadingState
                    title="Ładowanie sugestii"
                    message="Pobieramy Twoją historię zgłoszeń."
                  />
                </div>
              ) : mySuggestions.length === 0 ? (
                <div className="py-4">
                  <PageEmptyState
                    title="Brak sugestii"
                    message="Po wysłaniu propozycji pojawi się tutaj jej status."
                  />
                </div>
              ) : (
                <div className="mt-4 space-y-3">
                  {mySuggestions.map((item) => (
                    <article
                      key={item.equipmentTypeSuggestionId}
                      className="rounded-2xl border border-primary-dark-300/70 bg-primary-dark-100/55 p-4"
                    >
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="text-base font-semibold text-primary-light-100">
                              {item.suggestedName || 'Brak nazwy'}
                            </h3>
                            {getSuggestionStatusBadge(item.status)}
                          </div>
                          <p className="mt-1 text-xs text-primary-light-400">
                            {formatTimestamp(item.createdAt)}
                          </p>
                        </div>
                        <Badge variant="neutral" size="sm">
                          {item.canonicalShortCode || item.canonicalName || item.suggestedNameKey}
                        </Badge>
                      </div>

                      {item.notes && (
                        <p className="mt-3 rounded-xl border border-primary-dark-300/60 bg-primary-dark-200/50 p-3 text-sm text-primary-light-300">
                          {item.notes}
                        </p>
                      )}

                      {item.status === 'accepted' && (
                        <>
                          <p className="mt-3 text-sm text-status-success-500">
                            Zgłoszenie zostało zaakceptowane i utworzyło canonical rekord.
                          </p>
                          {item.resolvedAt && (
                            <p className="mt-1 text-xs text-primary-light-400">
                              Decyzja: {formatTimestamp(item.resolvedAt)}
                            </p>
                          )}
                        </>
                      )}

                      {item.status === 'rejected' && (
                        <>
                          <p className="mt-3 text-sm text-status-error-500">
                            {item.rejectionReason || 'Sugestia została odrzucona przez admina.'}
                          </p>
                          {item.resolvedAt && (
                            <p className="mt-1 text-xs text-primary-light-400">
                              Decyzja: {formatTimestamp(item.resolvedAt)}
                            </p>
                          )}
                        </>
                      )}
                    </article>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>
    </ServiceWorkspaceShell>
  );
};

export default ServiceEquipmentTypesPage;
